import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Play, Trash2, Video as VideoIcon, Pencil, Eye, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import videoService, { VideoDoc } from '../services/videoService';
import VideoPreviewModal from '../components/videos/VideoPreviewModal';

const Videos: React.FC = () => {
  const navigate = useNavigate();
  const [videos, setVideos] = useState<VideoDoc[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewTitle, setPreviewTitle] = useState<string>('');
  const [previewYoutubeUrl, setPreviewYoutubeUrl] = useState<string>('');

  useEffect(() => {
    const fetchVideos = async () => {
      setLoading(true);
      try {
        const result = await videoService.getVideos({ isPublished: true });
        setVideos(result.data || []);
      } finally {
        setLoading(false);
      }
    };
    fetchVideos();
  }, []);

  const filteredVideos = useMemo(() => {
    const s = searchTerm.trim().toLowerCase();
    if (!s) return videos;
    return videos.filter((v) => {
      return (
        (v.title || '').toLowerCase().includes(s) ||
        (v.description || '').toLowerCase().includes(s) ||
        (v.category || '').toLowerCase().includes(s)
      );
    });
  }, [videos, searchTerm]);

  const openPreview = (video: VideoDoc) => {
    setPreviewTitle(video.title || 'Video');
    setPreviewYoutubeUrl(video.youtubeUrl || '');
    setShowPreviewModal(true);
  };

  const handleDelete = async (video: VideoDoc) => {
    const confirmed = window.confirm('Are you sure you want to delete this video? This will remove the record.');
    if (!confirmed) return;

    try {
      // Try to delete thumbnail from storage (best-effort)
      if (video.thumbnailUrl) {
        try {
          await videoService.deleteThumbnailFromStorage(video.thumbnailUrl);
        } catch {
          // ignore storage deletion errors
        }
      }
      await videoService.deleteVideo(video.id);
      setVideos((prev) => prev.filter((v) => v.id !== video.id));
    } catch (e) {
      // toast is handled by service
      console.error('Failed to delete video', e);
    }
  };

  const formatCreatedAt = (value: any): string => {
    if (!value) return 'N/A';
    const fmt = (d: Date) => {
      const datePart = d.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
      const timePart = d.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
      });
      return `${datePart} at ${timePart}`;
    };
    try {
      // Firestore Timestamp
      if (value?.toDate && typeof value.toDate === 'function') {
        return fmt(value.toDate());
      }
      // JS Date
      if (value instanceof Date) {
        return fmt(value);
      }
      // epoch seconds/millis
      if (typeof value === 'number') {
        const ms = value < 10000000000 ? value * 1000 : value;
        return fmt(new Date(ms));
      }
      // ISO string
      if (typeof value === 'string') {
        const d = new Date(value);
        return isNaN(d.getTime()) ? 'N/A' : fmt(d);
      }
      return 'N/A';
    } catch {
      return 'N/A';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-heading text-primary-800">Videos</h1>
          <p className="text-gray-600 mt-1">Manage your video library</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/videos/new')}>
          <Plus className="w-4 h-4 mr-2" />
          Add Video
        </button>
      </div>

      {/* Search */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search videos..."
                  className="input pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-primary-100 rounded-apple">
                <VideoIcon className="w-5 h-5 text-primary-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Total Videos</p>
                <p className="text-lg font-semibold text-gray-900">{videos.length}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-success-100 rounded-apple">
                <Play className="w-5 h-5 text-success-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Short</p>
                <p className="text-lg font-semibold text-gray-900">
                  {videos.filter((v) => v.category === 'short').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-warning-100 rounded-apple">
                <VideoIcon className="w-5 h-5 text-warning-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Long Video</p>
                <p className="text-lg font-semibold text-gray-900">
                  {videos.filter((v) => v.category === 'longvideo').length}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Video</th>
                  <th>Category</th>
                  <th>Created</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="p-6 text-sm text-gray-600">
                      Loading...
                    </td>
                  </tr>
                ) : (
                  filteredVideos.map((video) => (
                    <tr key={video.id}>
                      <td>
                        <div className="flex items-center">
                          <div className="w-12 h-12 bg-gray-200 rounded-apple flex items-center justify-center mr-4 overflow-hidden">
                            {video.thumbnailUrl ? (
                              <img src={video.thumbnailUrl} alt={video.title} className="w-full h-full object-cover" />
                            ) : (
                              <VideoIcon className="w-6 h-6 text-gray-400" />
                            )}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{video.title}</div>
                            <div className="text-sm text-gray-500 truncate max-w-xs">{video.description}</div>
                          </div>
                        </div>
                      </td>
                      <td className="text-sm text-gray-900">{video.category}</td>
                      <td className="text-sm text-gray-900">{formatCreatedAt((video as any).createdAt)}</td>
                      <td>{video.isPublished ? <span className="badge badge-success">Published</span> : <span className="badge badge-warning">Draft</span>}</td>
                      <td>
                        <div className="flex items-center space-x-2">
                          <button
                            className="p-1 text-gray-400 hover:text-primary-600"
                            onClick={() => openPreview(video)}
                            title="Play"
                          >
                            <Play className="w-4 h-4" />
                          </button>
                          <button
                            className="p-1 text-gray-400 hover:text-primary-600"
                            onClick={() => navigate(`/videos/${video.id}/view`)}
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            className="p-1 text-gray-400 hover:text-primary-600"
                            onClick={() => navigate(`/videos/${video.id}/edit`)}
                            title="Edit"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            className="p-1 text-gray-400 hover:text-error-600"
                            onClick={() => handleDelete(video)}
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <VideoPreviewModal
        isOpen={showPreviewModal}
        title={previewTitle}
        youtubeUrl={previewYoutubeUrl}
        onCancel={() => setShowPreviewModal(false)}
        onConfirm={() => setShowPreviewModal(false)}
        showActions={false}
      />
    </div>
  );
};

export default Videos;

